import { useRef } from "react";
import { useEventListener } from "@tellescope/react-hooks";
import type { FormResponse } from "@tellescope/types-models";
import { useOrganization } from "../../../../shared/store/organizationStore";
import { useIframeToken } from "../../../hooks/useIframeToken";
import { buildFormCompletionURL, getLegacyPortalURL } from "../../../../config/portal";

/** What the legacy forms hook posts to its parent window once `submit_form_response` succeeds. */
const isFormSubmittedMessage = (data: unknown): boolean =>
  typeof data === "object" && data !== null && "type" in data && data.type === "Form Submitted";

interface UseFormAccessCodeIframeArgs {
  formResponse: Pick<FormResponse, "formId" | "accessCode" | "automationStepId">;
  onSubmitted: () => void;
}

export const useFormAccessCodeIframe = ({ formResponse, onSubmitted }: UseFormAccessCodeIframeArgs) => {
  const organization = useOrganization();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { token, isLoading, error } = useIframeToken();

  const iframeUrl =
    organization && token && formResponse.accessCode
      ? buildFormCompletionURL({
          organization: { subdomain: organization.subdomain },
          formId: formResponse.formId,
          accessCode: formResponse.accessCode,
          authToken: token,
          automationStepId: formResponse.automationStepId,
          hideBg: true,
          startAtNext: true,
        })
      : null;

  useEventListener("message", (event) => {
    if (event.origin !== new URL(getLegacyPortalURL()).origin) return;
    if (event.source !== iframeRef.current?.contentWindow) return;
    if (isFormSubmittedMessage(event.data)) onSubmitted();
  });

  return { iframeRef, iframeUrl, isLoading, error };
};
